import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  Calendar,
  Award,
  TrendingUp,
  Clock,
  Target,
  ChevronRight,
  Star,
  Rocket,
  Users
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StatsCard } from '../components/dashboard/StatsCard';
import { StatsCardDialog } from '../components/dashboard/StatsCardDialog';
import { CourseCard } from '../components/dashboard/CourseCard';
import OnboardingGuide from '../components/dashboard/OnboardingGuide';
import { VideoAssignmentsWidget } from '../components/dashboard/VideoAssignmentsWidget';
import { UpcomingMeetingsWidget } from '../components/dashboard/UpcomingMeetingsWidget';
import { MenteesWidget } from '../components/dashboard/MenteesWidget';
import { AdminQuickActionsWidget } from '../components/dashboard/AdminQuickActionsWidget';
import { CoordinatorQuickActionsWidget } from '../components/dashboard/CoordinatorQuickActionsWidget';
import { PrincipalQuickActionsWidget } from '../components/dashboard/PrincipalQuickActionsWidget';
import { InstructorQuickActionsWidget } from '../components/dashboard/InstructorQuickActionsWidget';
import { MentorQuickActionsWidget } from '../components/dashboard/MentorQuickActionsWidget';
import { UserQuickActionsWidget } from '../components/dashboard/UserQuickActionsWidget';
import { PendingApprovalsWidget } from '../components/dashboard/PendingApprovalsWidget';
import { ProgressTracker } from '../components/engagement/ProgressTracker';
import { Leaderboard } from '../components/engagement/Leaderboard';
import type { User, Course, DashboardStats } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/useRole";

// Real API calls to Supabase
const User = {
  me: async () => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('No authenticated user');
    
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) throw profileError;
    const profileData = profile as any;
    return {
      id: user.id,
      email: user.email,
      full_name: `${profileData.first_name} ${profileData.last_name}`,
      job_title: profileData.position_title,
      site: profileData.school_name,
      role: profileData.is_admin ? 'admin' : 'user',
      district_id: profileData.district_id,
      pd_hours_completed: profileData.pd_hours_completed || 0,
      required_pd_hours: profileData.required_pd_hours || 40,
      onboarding_state: profileData.onboarding_state || {},
      ...profileData
    };
  },
  updateMyUserData: async (data: any) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('No authenticated user');
    
    const { data: result, error } = await supabase
      .from('profiles')
      .update(data)
      .eq('id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    const resultData = result as any;
    return {
      id: user.id,
      email: user.email,
      full_name: `${resultData.first_name} ${resultData.last_name}`,
      job_title: resultData.position_title,
      site: resultData.school_name,
      role: resultData.is_admin ? 'admin' : 'user',
      district_id: resultData.district_id,
      pd_hours_completed: resultData.pd_hours_completed || 0,
      required_pd_hours: resultData.required_pd_hours || 40,
      onboarding_state: resultData.onboarding_state || {},
      ...resultData
    };
  },
};

const Course = {
  list: async () => {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        course_categories(name)
      `)
      .eq('status', 'published');
    
    if (error) throw error;
    return data.map(course => ({
      ...course,
      category: course.course_categories?.name || 'other',
      delivery_method: course.is_online ? 'synchronous_online' : 'in_person',
      pd_hours: course.duration_hours || 0,
      target_audience: ['all'],
      status: 'active'
    }));
  },
  get: async (id: string) => {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        course_categories(name)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return {
      ...data,
      category: data.course_categories?.name || 'other',
      delivery_method: data.is_online ? 'synchronous_online' : 'in_person',
      pd_hours: data.duration_hours || 0,
      target_audience: ['all'],
      status: 'active'
    };
  },
};

const Enrollment = {
  filter: async (filter: any) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .match(filter);
    
    if (error) throw error;
    return data.map(enrollment => ({
      ...enrollment,
      user_id: enrollment.participant_id,
      course_id: enrollment.course_id,
      status: enrollment.status,
      completion_date: enrollment.completion_date,
      enrollment_date: enrollment.enrollment_date
    }));
  },
};

const District = {
  list: async () => {
    const { data, error } = await supabase
      .from('districts')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },
  create: async (data: any) => {
    const { data: result, error } = await supabase
      .from('districts')
      .insert(data)
      .select()
      .single();
    
    if (error) throw error;
    return result;
  },
};

const createPageUrl = (page: string) => `/${page.toLowerCase()}`;

export default function Dashboard() {
  const { role, isAdmin, isCoordinator, isPrincipal, isInstructor, isMentor, loading: roleLoading } = useRole();
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    completedCourses: 0,
    enrolledCourses: 0,
    pdHours: 0,
    requiredHours: 40
  });
  const [recentCourses, setRecentCourses] = useState<Course[]>([]);
  const [upcomingCourses, setUpcomingCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnboardingGuide, setShowOnboardingGuide] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<any>(null);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  
  // Role-specific data
  const [videoAssignments, setVideoAssignments] = useState<any[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<any[]>([]);
  const [mentees, setMentees] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [roleDataLoading, setRoleDataLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
    window.addEventListener('focus', loadDashboardData);
    return () => {
      window.removeEventListener('focus', loadDashboardData);
    };
  }, []);

  useEffect(() => {
    if (user && !roleLoading) {
      loadRoleSpecificData();
    }
  }, [user, role, roleLoading]);

  const handleOnboardingUpdate = async (update: any) => {
    const currentState = user?.onboarding_state || {};
    const newState = { ...currentState, ...update };
    
    if (update.dismissed) {
      setShowOnboardingGuide(false);
    }
    
    try {
      await User.updateMyUserData({ onboarding_state: newState });
      setUser(prevUser => prevUser ? { ...prevUser, onboarding_state: newState } : null);
    } catch (error) {
      console.error("Failed to update onboarding state:", error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const currentUser = await User.me() as User;
      console.log(`Dashboard - Loading user data:`, currentUser);

      // Admin district assignment logic
      if (currentUser.role === 'admin' && !currentUser.district_id) {
        console.log('Dashboard - Admin without district, assigning...');
        toast.info("Performing first-time setup: Assigning your account to a district...");
        let targetDistrict;
        const districts = await District.list();
        if (districts.length > 0) {
          targetDistrict = districts[0];
        } else {
          toast.info("No districts found. Creating a default demo district...");
          targetDistrict = await District.create({
            name: "Default Demo District",
            domain: "demo-district.edu",
            subscription_status: "active"
          });
          toast.success("Default Demo District created.");
        }
        
        await User.updateMyUserData({ district_id: targetDistrict.id });
        const updatedUser = await User.me() as User;
        setUser(updatedUser);
        toast.success("District setup complete! Your admin account is now assigned to " + targetDistrict.name + ".");
        return;
      }

      // PD hours initialization
      let userToUse = currentUser;
      if (currentUser.pd_hours_completed === undefined || currentUser.pd_hours_completed === null) {
        console.log(`Dashboard - Initializing PD hours to 0`);
        try {
          await User.updateMyUserData({ pd_hours_completed: 0 });
          userToUse = await User.me() as User;
          console.log(`Dashboard - PD hours initialized in backend and refetched.`);
        } catch (error) {
          console.error('Error initializing PD hours in backend:', error);
          userToUse = { ...currentUser, pd_hours_completed: 0 };
          console.log(`Dashboard - Fallback: PD hours set to 0 locally due to backend error.`);
        }
      }

      setUser(userToUse);
      setShowOnboardingGuide(!userToUse.onboarding_state?.dismissed);

      // Load course data
      const userEnrollments = await Enrollment.filter({ participant_id: userToUse.id });
      const completedCourses = userEnrollments.filter((e: any) => e.status === 'completed');
      const courseList = await Course.list();
      const activeCourses = courseList.filter((c: any) => c.status === 'active');
      
      // Store data for dialog usage
      setEnrollments(userEnrollments);
      setAllCourses(courseList.map(course => ({
        ...course,
        category: course.course_categories?.name || 'other',
        delivery_method: course.is_online ? 'synchronous_online' : 'in_person',
        pd_hours: course.duration_hours || 0,
        target_audience: ['all'],
        status: course.status === 'published' ? 'active' : course.status === 'draft' ? 'inactive' : 'approved'
      })));

      // Load upcoming courses
      const upcoming = await Promise.all(
        userEnrollments
          .filter((e: any) => e.status === 'enrolled')
          .slice(0, 3)
          .map(async (e: any) => {
            const course = await Course.get(e.course_id) as Course;
            return { ...course, enrollment: e } as Course;
          })
      );

      // Load recent completed courses
      const recent = await Promise.all(
        completedCourses
          .sort((a: any, b: any) => new Date(b.completion_date).getTime() - new Date(a.completion_date).getTime())
          .slice(0, 3)
          .map(async (e: any) => {
            const course = await Course.get(e.course_id) as Course;
            return { ...course, enrollment: e } as Course;
          })
      );

      setStats({
        totalCourses: activeCourses.length,
        completedCourses: completedCourses.length,
        enrolledCourses: userEnrollments.filter((e: any) => e.status === 'enrolled').length,
        pdHours: userToUse.pd_hours_completed || 0,
        requiredHours: userToUse.required_pd_hours || 40
      });

      setUpcomingCourses(upcoming);
      setRecentCourses(recent);

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error("Failed to load dashboard data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const loadRoleSpecificData = async () => {
    if (!user) return;
    setRoleDataLoading(true);
    
    try {
      // Load video assignments for teachers
      if (!isAdmin) {
        const { data: assignments } = await supabase
          .from('video_assignments')
          .select('id, notes, video_id, assigned_by, teacher_id')
          .eq('teacher_id', user.id)
          .limit(5);
        
        if (assignments) {
          const assignmentsWithDetails = await Promise.all(
            assignments.map(async (a) => {
              const [video, assigner] = await Promise.all([
                supabase.from('videos').select('title').eq('id', a.video_id).single(),
                supabase.from('profiles').select('first_name, last_name').eq('id', a.assigned_by).single()
              ]);
              
              return {
                id: a.id,
                video_title: video.data?.title || 'Untitled Video',
                assigned_by_name: `${assigner.data?.first_name} ${assigner.data?.last_name}`,
                notes: a.notes
              };
            })
          );
          setVideoAssignments(assignmentsWithDetails);
        }
      }

      // Load upcoming meetings for teachers/mentors
      if (!isAdmin) {
        const { data: meetings } = await supabase
          .from('meetings')
          .select('id, meeting_type, scheduled_date, observer_id, observee_id')
          .or(`observer_id.eq.${user.id},observee_id.eq.${user.id}`)
          .gte('scheduled_date', new Date().toISOString())
          .order('scheduled_date', { ascending: true })
          .limit(5);
        
        if (meetings) {
          const meetingsWithNames = await Promise.all(
            meetings.map(async (m) => {
              const otherId = m.observer_id === user.id ? m.observee_id : m.observer_id;
              const { data: otherPerson } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', otherId)
                .single();
              
              return {
                id: m.id,
                meeting_type: m.meeting_type,
                scheduled_date: m.scheduled_date,
                other_person_name: `${otherPerson?.first_name} ${otherPerson?.last_name}`,
                is_observer: m.observer_id === user.id
              };
            })
          );
          setUpcomingMeetings(meetingsWithNames);
        }
      }

      // Load mentees for mentors
      if (isMentor) {
        const { data: assignments } = await supabase
          .from('mentor_assignments')
          .select('id, academic_year, mentee_id, mentor_id')
          .eq('mentor_id', user.id)
          .eq('status', 'active');
        
        if (assignments) {
          const menteesData = await Promise.all(
            assignments.map(async (assignment) => {
              const [mentee, meetings] = await Promise.all([
                supabase.from('profiles').select('first_name, last_name').eq('id', assignment.mentee_id).single(),
                supabase.from('meetings').select('id').eq('observer_id', user.id).eq('observee_id', assignment.mentee_id).eq('status', 'completed')
              ]);
              
              return {
                id: assignment.id,
                mentee_name: `${mentee.data?.first_name} ${mentee.data?.last_name}`,
                academic_year: assignment.academic_year,
                meetings_completed: meetings.data?.length || 0,
                required_meetings: 8
              };
            })
          );
          setMentees(menteesData);
        }
      }

      // Load pending approvals for admins
      if (isAdmin) {
        const [proposals, requests] = await Promise.all([
          supabase.from('course_proposals').select('id').eq('status', 'pending'),
          supabase.from('chat_offline_requests').select('id').eq('status', 'pending')
        ]);
        
        setPendingApprovals([
          { type: 'course_proposal', count: proposals.data?.length || 0, path: '/proposal-queue' },
          { type: 'user_request', count: requests.data?.length || 0, path: '/admin-chat' }
        ]);
      }
    } catch (error) {
      console.error('Error loading role-specific data:', error);
    } finally {
      setRoleDataLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-64 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = Math.min(100, ((stats.pdHours || 0) / (stats.requiredHours || 1)) * 100);

  const handleStatsCardClick = async (type: 'pd_hours' | 'enrolled' | 'completed' | 'available') => {
    let data = {};
    
    switch (type) {
      case 'pd_hours':
        const completedCoursesWithDetails = await Promise.all(
          enrollments
            .filter((e: any) => e.status === 'completed')
            .slice(0, 5)
            .map(async (e: any) => {
              const course = await Course.get(e.course_id);
              return { ...course, completion_date: e.completion_date, enrollment: e };
            })
        );
        data = {
          completed: stats.pdHours,
          required: stats.requiredHours,
          recentCourses: completedCoursesWithDetails
        };
        break;
        
      case 'enrolled':
        const enrolledCoursesWithDetails = await Promise.all(
          enrollments
            .filter((e: any) => e.status === 'enrolled')
            .map(async (e: any) => {
              const course = await Course.get(e.course_id);
              return { ...course, enrollment_date: e.enrollment_date, enrollment: e };
            })
        );
        data = {
          count: stats.enrolledCourses,
          courses: enrolledCoursesWithDetails
        };
        break;
        
      case 'completed':
        const completedWithDetails = await Promise.all(
          enrollments
            .filter((e: any) => e.status === 'completed')
            .map(async (e: any) => {
              const course = await Course.get(e.course_id);
              return { ...course, completion_date: e.completion_date, enrollment: e };
            })
        );
        const totalHours = completedWithDetails.reduce((sum, course) => sum + (course.duration_hours || 0), 0);
        const totalEnrolled = enrollments.length;
        const completionRate = totalEnrolled > 0 ? Math.round((stats.completedCourses / totalEnrolled) * 100) : 0;
        
        data = {
          count: stats.completedCourses,
          courses: completedWithDetails,
          totalHours,
          completionRate
        };
        break;
        
      case 'available':
        data = {
          count: stats.totalCourses,
          courses: allCourses
        };
        break;
    }
    
    setDialogData({ type, title: type === 'pd_hours' ? 'PD Hours Progress' : type === 'enrolled' ? 'Active Enrollments' : type === 'completed' ? 'Completed Courses' : 'Available Courses', data });
    setDialogOpen(true);
  };

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">
      {showOnboardingGuide && (
        <OnboardingGuide user={user} onOnboardingUpdate={handleOnboardingUpdate} />
      )}

      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-foreground">
            Welcome back, {user?.full_name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-muted-foreground">
            {user?.job_title} • {user?.site}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowOnboardingGuide(!showOnboardingGuide)}
            className="transition-all hover:scale-105"
          >
            <Rocket className="w-4 h-4 mr-2" />
            {showOnboardingGuide ? 'Hide Tour' : 'Show Tour'}
          </Button>
          <Link to={createPageUrl("CourseCatalog")}>
            <Button className="bg-gradient-primary border-0 shadow-glow transition-all hover:scale-105 hover:shadow-xl">
              <BookOpen className="w-4 h-4 mr-2" />
              Browse Catalog
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="PD Hours Progress"
          value={`${user?.pd_hours_completed || 0} / ${user?.required_pd_hours || 40}`}
          description={`${Math.round(progressPercentage)}% Complete`}
          icon={Award}
          variant="primary"
          progress={progressPercentage}
          onClick={() => handleStatsCardClick('pd_hours')}
        />
        
        <StatsCard
          title="Active Enrollments"
          value={stats.enrolledCourses}
          description="Courses in progress"
          icon={Calendar}
          onClick={() => handleStatsCardClick('enrolled')}
        />
        
        <StatsCard
          title="Completed Courses"
          value={stats.completedCourses}
          description="Great progress!"
          icon={Target}
          onClick={() => handleStatsCardClick('completed')}
        />
        
        <StatsCard
          title="Available Courses"
          value={stats.totalCourses}
          description="Ready to enroll"
          icon={BookOpen}
          onClick={() => handleStatsCardClick('available')}
        />
      </div>

      {/* Stats Dialog */}
      {dialogData && (
        <StatsCardDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          title={dialogData.title}
          type={dialogData.type}
          data={dialogData.data}
        />
      )}

      {/* Role-Specific Widgets */}
      {!roleLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Admin widgets */}
          {isAdmin && (
            <>
              <AdminQuickActionsWidget />
              <PendingApprovalsWidget 
                approvals={pendingApprovals} 
                loading={roleDataLoading} 
              />
            </>
          )}
          
          {/* Coordinator widgets */}
          {isCoordinator && !isAdmin && (
            <>
              <CoordinatorQuickActionsWidget />
              <PendingApprovalsWidget 
                approvals={pendingApprovals} 
                loading={roleDataLoading} 
              />
            </>
          )}
          
          {/* Principal widgets */}
          {isPrincipal && !isCoordinator && (
            <>
              <PrincipalQuickActionsWidget />
              <UpcomingMeetingsWidget 
                meetings={upcomingMeetings} 
                loading={roleDataLoading} 
              />
            </>
          )}
          
          {/* Instructor widgets */}
          {isInstructor && !isPrincipal && (
            <>
              <InstructorQuickActionsWidget />
              <VideoAssignmentsWidget 
                assignments={videoAssignments} 
                loading={roleDataLoading} 
              />
              <UpcomingMeetingsWidget 
                meetings={upcomingMeetings} 
                loading={roleDataLoading} 
              />
            </>
          )}
          
          {/* Mentor widgets */}
          {isMentor && !isInstructor && (
            <>
              <MentorQuickActionsWidget />
              <MenteesWidget 
                mentees={mentees} 
                loading={roleDataLoading} 
              />
              <UpcomingMeetingsWidget 
                meetings={upcomingMeetings} 
                loading={roleDataLoading} 
              />
            </>
          )}
          
          {/* Regular user widgets */}
          {role === 'user' && (
            <>
              <UserQuickActionsWidget />
              <VideoAssignmentsWidget 
                assignments={videoAssignments} 
                loading={roleDataLoading} 
              />
              <UpcomingMeetingsWidget 
                meetings={upcomingMeetings} 
                loading={roleDataLoading} 
              />
            </>
          )}
        </div>
      )}

      {/* Progress & Gamification - All Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProgressTracker />
        <Leaderboard />
      </div>

      {/* Course Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upcoming Courses */}
        <Card className="shadow-lg border-0 bg-gradient-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Upcoming Courses
              </CardTitle>
              <Link to={createPageUrl("MyLearning")}>
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary-glow transition-colors">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {upcomingCourses.length > 0 ? (
              upcomingCourses.map((course) => (
                <CourseCard key={course.id} course={course} variant="upcoming" />
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-muted-foreground font-medium">No upcoming courses</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Browse the catalog to enroll in new courses
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recently Completed */}
        <Card className="shadow-lg border-0 bg-gradient-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-success" />
                Recently Completed
              </CardTitle>
              <Link to={createPageUrl("Transcript")}>
                <Button variant="ghost" size="sm" className="text-success hover:text-success/80 transition-colors">
                  View Transcript <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {recentCourses.length > 0 ? (
              recentCourses.map((course) => (
                <CourseCard key={course.id} course={course} variant="completed" />
              ))
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-muted-foreground font-medium">No completed courses yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete your first course to see it here
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}